import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageTitle from '../components/PageTitle';
import { HOME_WIDGETS, HOME_WIDGET_DEFAULT_ENABLED_IDS } from '../config/homeWidgets';
import {
  getHomeWidgetEnabledIds,
  setHomeWidgetEnabledIds,
  getDebugEnabled,
  setDebugEnabled,
} from '../features/scrabble/services/settingsService';

function SettingsPage() {
  const [enabledIds, setEnabledIds] = React.useState(() =>
    getHomeWidgetEnabledIds(HOME_WIDGET_DEFAULT_ENABLED_IDS)
  );
  const [isDebugEnabled, setIsDebugEnabled] = React.useState(() => getDebugEnabled());

  const handleToggle = (id) => {
    const next = enabledIds.includes(id)
      ? enabledIds.filter((i) => i !== id)
      : [...enabledIds, id];
    setEnabledIds(next);
    setHomeWidgetEnabledIds(next);
  };

  const handleDebugToggle = () => {
    const next = !isDebugEnabled;
    setIsDebugEnabled(next);
    setDebugEnabled(next);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 5 },
        background: 'linear-gradient(180deg, #f8fbff 0%, #f2f6fd 100%)',
      }}
    >
      <Stack spacing={2.5} sx={{ maxWidth: 640, mx: 'auto' }}>
        <PageTitle subtitle="Settings" />

        <Card
          sx={{
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.08)}`,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <TuneRoundedIcon color="primary" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Home Page Widgets
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which stat widgets cycle on your home page. Ticked widgets will rotate through
              one at a time.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={0.8}>
              {HOME_WIDGETS.map((widget) => {
                const checked = enabledIds.includes(widget.id);
                return (
                  <Box
                    key={widget.id}
                    onClick={() => handleToggle(widget.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: (theme) =>
                        `1px solid ${
                          checked
                            ? alpha(theme.palette.primary.main, 0.3)
                            : alpha(theme.palette.divider, 1)
                        }`,
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      backgroundColor: (theme) =>
                        checked ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                      transition: 'background-color 120ms, border-color 120ms',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleToggle(widget.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {widget.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {widget.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.08)}`,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <BugReportRoundedIcon color="primary" fontSize="small" />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Developer
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tools for debugging and inspecting app behaviour.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <Box
              onClick={handleDebugToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: (theme) =>
                  `1px solid ${
                    isDebugEnabled
                      ? alpha(theme.palette.primary.main, 0.3)
                      : alpha(theme.palette.divider, 1)
                  }`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                backgroundColor: (theme) =>
                  isDebugEnabled ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                transition: 'background-color 120ms, border-color 120ms',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDebugEnabled}
                    onChange={handleDebugToggle}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>Debug</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Log all API calls to the browser console.
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default SettingsPage;
